import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  message,
  Row,
  Col,
  Descriptions,
  Image,
  Statistic,
  Divider
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PlayCircleOutlined, 
  EyeOutlined,
  LinkOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { videoApi } from '../services/videoApi';

const { Title, Text, Paragraph } = Typography;

const VideoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: video, isLoading } = useQuery(
    ['video', id],
    () => videoApi.getById(id!),
    { enabled: !!id }
  );

  const incrementViewMutation = useMutation(
    () => videoApi.incrementView(id!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['video', id]);
      },
    }
  );

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'green', text: 'Active' },
      inactive: { color: 'orange', text: 'Inactive' },
      processing: { color: 'blue', text: 'Processing' },
      error: { color: 'red', text: 'Error' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayVideo = () => {
    if (video?.videoLink) {
      incrementViewMutation.mutate();
      window.open(video.videoLink, '_blank');
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!video) {
    return <div>Video not found</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/videos')}
          style={{ marginRight: 16 }}
        >
          Back to Videos
        </Button>
        <Title level={2} style={{ display: 'inline' }}>
          {video.title}
        </Title>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Video Information">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {video.imageUrl ? (
                    <Image
                      src={video.imageUrl}
                      alt={video.title}
                      style={{ 
                        width: '100%', 
                        maxWidth: 400, 
                        height: 225, 
                        objectFit: 'cover',
                        borderRadius: 8,
                        cursor: 'pointer'
                      }}
                      onClick={handlePlayVideo}
                      preview={false}
                      fallback="/images/video-placeholder.svg"
                    />
                  ) : (
                    <img 
                      src="/images/video-placeholder.svg" 
                      alt="Video placeholder"
                      style={{ 
                        width: '100%', 
                        maxWidth: 400, 
                        height: 225, 
                        objectFit: 'cover',
                        borderRadius: 8,
                        cursor: 'pointer',
                        margin: '0 auto'
                      }}
                      onClick={handlePlayVideo}
                    />
                  )}
                </div>
              </Col>
              
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={handlePlayVideo}
                    block
                  >
                    Play Video
                  </Button>
                  
                  <Button 
                    icon={<LinkOutlined />}
                    onClick={() => window.open(video.videoLink, '_blank')}
                    block
                  >
                    Open in New Tab
                  </Button>
                  
                  <Button 
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/videos/${video.id}/edit`)}
                    block
                  >
                    Edit Video
                  </Button>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Descriptions column={1}>
              <Descriptions.Item label="Title">{video.title}</Descriptions.Item>
              <Descriptions.Item label="Duration">{formatDuration(video.duration)}</Descriptions.Item>
              <Descriptions.Item label="Category">
                {video.category ? <Tag>{video.category}</Tag> : 'No category'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(video.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Published">
                <Tag color={video.isPublished ? 'green' : 'red'}>
                  {video.isPublished ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(video.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {new Date(video.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
            
            {video.description && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Description:</Text>
                <Paragraph style={{ marginTop: 8 }}>{video.description}</Paragraph>
              </div>
            )}

            {video.tags && video.tags.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Tags:</Text>
                <div style={{ marginTop: 8 }}>
                  {video.tags.map((tag, index) => (
                    <Tag key={index} style={{ marginBottom: 4 }}>{tag}</Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Statistics">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Statistic
                title="Views"
                value={video.viewCount}
                prefix={<EyeOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
              
              <Statistic
                title="Duration"
                value={formatDuration(video.duration)}
                prefix={<PlayCircleOutlined />}
              />
              
              <Statistic
                title="Status"
                value={video.status}
                valueStyle={{ 
                  color: video.status === 'active' ? '#52c41a' : 
                         video.status === 'inactive' ? '#faad14' : '#ff4d4f'
                }}
              />
            </Space>
          </Card>

          <Card title="Quick Actions" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                onClick={handlePlayVideo}
                block
              >
                Watch Video
              </Button>
              
              <Button 
                icon={<EditOutlined />}
                onClick={() => navigate(`/videos/${video.id}/edit`)}
                block
              >
                Edit Details
              </Button>
              
              <Button 
                icon={<LinkOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(video.videoLink);
                  message.success('Video link copied to clipboard!');
                }}
                block
              >
                Copy Link
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default VideoDetail;
